import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Menu,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { TbSearch } from "react-icons/tb";
import { NO_GOAL_COLOR, NO_GOAL_LABEL } from "utils/strategicGoals";

// Lives inside Menu.Dropdown, so it mounts on every open. Mantine's focus trap
// focuses the dropdown wrapper (it carries data-autofocus) in a setTimeout
// after mount; this one is queued later, so the search box wins and the user
// can type straight away. ArrowDown still hands focus to the first item.
const SearchBox = ({ value, onChange }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(
      () => inputRef.current?.focus({ preventScroll: true }),
      0
    );
    return () => clearTimeout(timer);
  }, []);

  return (
    <TextInput
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder="Search goals"
      aria-label="Search goals"
      icon={<TbSearch size={14} aria-hidden="true" />}
      size="xs"
    />
  );
};

// The body of the goal picker: a search box, the always-present "No goal"
// item, and the org catalog grouped by category. Rendered inside a <Menu>.
// It only reports the chosen payload — whether that becomes a write (the
// meeting page) or a staged edit (the Insights popup) is the caller's call.
const GoalMenuDropdown = ({ goals, onSelect }) => {
  const [search, setSearch] = useState("");

  // A goal is a { name, description } object on the category document.
  const matchesSearch = (value) =>
    String(value || "")
      .toLowerCase()
      .includes(search.toLowerCase());

  const filteredCategories = (goals || [])
    .map((category) => {
      const categoryNameMatches = matchesSearch(category?.name);
      const matchingGoals = (category?.goals || []).filter(
        (goal) => matchesSearch(goal?.name) || matchesSearch(goal?.description)
      );
      return {
        ...category,
        goals: categoryNameMatches && search ? category.goals : matchingGoals,
        __showCategory:
          categoryNameMatches || (matchingGoals && matchingGoals.length > 0),
      };
    })
    .filter((cat) => cat.__showCategory);

  return (
    <Menu.Dropdown sx={{ minWidth: 300, maxWidth: 300 }}>
      <Box
        p={"xs"}
        sx={{
          position: "sticky",
          top: 0,
          background: "white",
          zIndex: 1,
        }}
      >
        <SearchBox value={search} onChange={setSearch} />
      </Box>
      {/* Outside the search and the scroll area so it can never be filtered away. */}
      <Menu.Item
        role="menuitem"
        title="Clear the strategic goal for this meeting"
        onClick={() =>
          onSelect({
            goal: NO_GOAL_LABEL,
            color: NO_GOAL_COLOR,
            category: NO_GOAL_LABEL,
            categoryId: null,
          })
        }
      >
        <Flex align={"center"} gap={".5rem"}>
          <Box
            aria-hidden
            sx={(t) => ({
              width: "0.75rem",
              height: "0.75rem",
              borderRadius: "50%",
              backgroundColor: t.colors[NO_GOAL_COLOR][5],
            })}
          />
          <Text>{NO_GOAL_LABEL}</Text>
        </Flex>
      </Menu.Item>
      <Menu.Divider />
      <ScrollArea w={"100%"} h={"15rem"}>
        {filteredCategories && filteredCategories.length > 0 ? (
          filteredCategories.map((category) => (
            <Box key={category?.id}>
              <Menu.Label>
                <Flex align={"center"} gap={".5rem"}>
                  <Box
                    aria-hidden
                    sx={(t) => ({
                      width: "0.75rem",
                      height: "0.75rem",
                      borderRadius: "50%",
                      backgroundColor:
                        t.colors[category?.color]?.[5] || t.colors.gray[5],
                    })}
                  />
                  <Text fw={500}>
                    {String(category?.name || "Unnamed category")}
                  </Text>
                </Flex>
              </Menu.Label>
              {(category?.goals || []).map((goal) => (
                <Menu.Item
                  key={`${category?.id}-${goal?.name}`}
                  role="menuitem"
                  title={goal?.description || `Set goal to ${goal?.name}`}
                  onClick={() =>
                    onSelect({
                      goal: String(goal?.name || ""),
                      color: category?.color,
                      category: category?.name,
                      categoryId: category?.id,
                    })
                  }
                  sx={{ paddingLeft: "1.75rem" }}
                >
                  <Stack spacing={2} sx={{ width: "100%" }}>
                    <Text>{String(goal?.name || "")}</Text>
                    {goal?.description && (
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {goal.description}
                      </Text>
                    )}
                  </Stack>
                </Menu.Item>
              ))}
            </Box>
          ))
        ) : (
          <Text size="sm" c="dimmed" px={"xs"} py={4} aria-live="polite">
            No goals found
          </Text>
        )}
      </ScrollArea>
    </Menu.Dropdown>
  );
};

export default GoalMenuDropdown;
